<template>
  <section class="ren-decoration-channels-editor">
    <header>
      <div>
        <strong>频道列表</strong>
        <span>普通频道只填名称；填写跳转路由后点击频道直接打开对应页面（如微短剧 /market）。</span>
      </div>
      <RrButton
        type="primary"
        size="small"
        :disabled="readonly || items.length >= 12"
        @click="addItem">
        添加频道
      </RrButton>
    </header>
    <div class="ren-decoration-channels-editor__items">
      <article v-for="(item, index) in items" :key="index">
        <header>
          <div>
            <strong>{{ index + 1 }}. {{ item.name || '未命名频道' }}</strong>
            <span>{{ item.action ? `点击跳转 ${item.action}` : '切换资讯流频道' }}</span>
          </div>
          <div class="ren-decoration-tabbar-editor__actions">
            <RrButton
              size="small"
              type="default"
              :disabled="readonly || index === 0"
              title="上移"
              aria-label="上移"
              @click="moveItem(index, -1)">
              <ArrowUp :size="12" />
            </RrButton>
            <RrButton
              size="small"
              type="default"
              :disabled="readonly || index === items.length - 1"
              title="下移"
              aria-label="下移"
              @click="moveItem(index, 1)">
              <ArrowDown :size="12" />
            </RrButton>
            <RrButton
              size="small"
              type="primary" danger
              :disabled="readonly || items.length <= 1"
              title="删除"
              aria-label="删除"
              @click="removeItem(index)">
              <Trash2 :size="12" />
            </RrButton>
          </div>
        </header>
        <div class="ren-decoration-channels-editor__fields">
          <label>
            <span>频道名称</span>
            <RrInput
              :disabled="readonly"
              maxlength="12"
              placeholder="如 推荐"
              :value="item.name"
              @update:value="(val) => setItem(index, 'name', String(val))" />
          </label>
          <label>
            <span>跳转路由</span>
            <RrInput
              :disabled="readonly"
              maxlength="64"
              placeholder="跳转路由（可选），如 /market"
              :value="item.action"
              @update:value="(val) => setItem(index, 'action', String(val))" />
          </label>
        </div>
      </article>
    </div>
    <p class="ren-decoration-layout-hint">
      支持 1 至 12 个频道。保存为 JSON 数组；配置跳转路由的频道不会再切换资讯流，点击直接跳转。
    </p>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { ArrowDown, ArrowUp, Trash2 } from '@lucide/vue'
import { RrButton, RrInput } from '@xjlv2/ui'
import { parseNewsChannels, serializeNewsChannels, type NewsChannelItem } from '../../utils/news-channels'
import { mergeWidgetProps, type WidgetSettingsEmits, type WidgetSettingsProps } from '../types'

const props = withDefaults(defineProps<WidgetSettingsProps>(), {
  readonly: false,
})

const emit = defineEmits<WidgetSettingsEmits>()

const items = computed(() => parseNewsChannels(props.component.props?.channels))

function commit(next: NewsChannelItem[]) {
  emit('update:props', mergeWidgetProps(props.component, { channels: serializeNewsChannels(next) }))
}

function setItem(index: number, key: keyof NewsChannelItem, value: string) {
  commit(items.value.map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: value } : item)))
}

function addItem() {
  if (items.value.length >= 12) return
  commit([...items.value, { name: `频道${items.value.length + 1}`, action: '' }])
}

function moveItem(index: number, offset: -1 | 1) {
  const nextIndex = index + offset
  if (nextIndex < 0 || nextIndex >= items.value.length) return
  const next = items.value.slice()
  const [item] = next.splice(index, 1)
  next.splice(nextIndex, 0, item)
  commit(next)
}

function removeItem(index: number) {
  if (items.value.length <= 1) return
  commit(items.value.filter((_, itemIndex) => itemIndex !== index))
}
</script>
